import { useMemo } from 'react';

import {
  applyInputIconStrokeWidth,
  InputErrorProps,
  InputSize,
} from '@xemplo/input-utils';

import { parseWidthCssVar } from './xemplo-input.helpers';
import { XemploInputProps } from './xemplo-input.types';

/**
 * TODO: Fix this so we have better memoization.
 * This hook depends on the props and it can have hundreds of props
 * and not all of them are relevant, but they would cause re-computation anyway.
 */
export function useInputFieldMetadata(props: XemploInputProps) {
  const { inputSize = InputSize.Default, leadingIcon, trailingIcon } = props;

  const styledLeadingIcon = useMemo(
    () => applyInputIconStrokeWidth(leadingIcon, inputSize),
    [leadingIcon, inputSize],
  );
  const styledTrailingIcon = useMemo(
    () => applyInputIconStrokeWidth(trailingIcon, inputSize),
    [trailingIcon, inputSize],
  );

  return useMemo(() => {
    const {
      id,
      name,
      inputSize = InputSize.Default,
      label,
      width,
      errorMessage,
      description,
      placeholder,
      leadingIcon,
      trailingIcon,
      className,
      disabled,
      readOnly,
      wrapperRef, // This is to avoid passing this to the actual input element
      wrapperId,
      containerOnClick,
      disableDescriptionOrError,
      disableErrorIcon = false,
      ...restProps
    } = props;

    const inputId = id ?? name;
    const isHiddenLabel =
      inputSize === InputSize.Compact || !label || label.trim() === '';
    const hasError = !!errorMessage;
    const errorId = hasError ? `${inputId}-error` : undefined;
    const descriptionId = description ? `${inputId}-description` : undefined;
    const ariaDescribedBy = hasError ? errorId : descriptionId;

    const ariaProps = {
      ...(hasError && { 'aria-invalid': true }),
      ...(disabled && { 'aria-disabled': true }),
      ...(readOnly && { 'aria-readonly': true }),
      ...(ariaDescribedBy && { 'aria-describedby': ariaDescribedBy }),
    } satisfies React.AriaAttributes;

    const inputProps = {
      ...restProps,
      ...ariaProps,
      id: inputId,
      name,
      disabled,
      readOnly,
      placeholder: placeholder ?? ' ',
    } satisfies React.InputHTMLAttributes<HTMLInputElement>;

    const maybeErrorOrDescriptionProps = {
      description,
      descriptionProps: descriptionId ? { id: descriptionId } : undefined,
      error: hasError,
      errorMessage,
      errorMessageProps: errorId ? { id: errorId } : undefined,
      testId: inputId,
      disableDescriptionOrError,
    } satisfies InputErrorProps;

    return {
      containerOnClick,
      wrapperId,
      inputProps,
      maybeErrorOrDescriptionProps,
      hasError,
      inputId,
      isHiddenLabel,
      width: parseWidthCssVar(width),

      // Raw props for convenience
      className,
      disabled,
      inputSize,
      label,
      leadingIcon: styledLeadingIcon,
      readOnly,
      trailingIcon: styledTrailingIcon,
      showErrorIcon: hasError && !disableErrorIcon,
    };
  }, [props, styledLeadingIcon, styledTrailingIcon]);
}
